'use client';

import { type ElementType, forwardRef, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot } from '../slot';
import { TagRootContext } from './context/tag-root.context';
import { tagRecipe } from './styles/tag-root.styles';
import type { TagRootPropsInterface } from './types/tag-root.types';

/**
 * @since 10.0.0
 */

export const TagRoot = forwardRef<HTMLSpanElement, TagRootPropsInterface>((props, forwardedRef) => {
  const {
    tone = 'gray',
    css: cssProp,
    children,
    asChild = false,
    size = 'md',
    className,
    ...rest
  } = props;

  const styles = css(tagRecipe.raw({ tone, size }), cssProp);

  const Component: ElementType = asChild ? Slot : 'span';

  const contextValue = useMemo(() => ({ size, tone }), [size, tone]);

  return (
    <TagRootContext.Provider value={contextValue}>
      <Component
        ref={forwardedRef}
        data-c11n-component="Tag.Root"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_tag__root', styles, className)}
        {...rest}
      >
        {children}
      </Component>
    </TagRootContext.Provider>
  );
});

TagRoot.displayName = 'Tag.Root';
